import { Component, computed, effect, inject, input, OnInit, output, PLATFORM_ID, signal } from '@angular/core';
import { ButtonLoadingDirective, IconComponent } from '@gains-com/ui-angular';
import { MatButton } from '@angular/material/button';
import { KycService } from '../../core/kyc.service.js';
import { KycVerificationViewService } from '../../core/view-states/kyc-verification-view.service.js';
import { toSignal } from '@angular/core/rxjs-interop';
import { LOGGER_FACTORY } from '../../core/logger/logger-factory.provider';
import { environment } from '../../../environments/environment.js';
import { LottieAnimationComponent } from '../lottie-animation/lottie-animation.component.js';
import { AnalyticsEvent, KycVerification } from '@gains-com/shared';
import { KycVerificationUpdateRTM } from '@gains-com/realtime';
import { AuthService } from '../../core/auth.service.js';
import { Analytics } from '../../core/analytics/analytics.service.js';
import { isPlatformBrowser } from '@angular/common';

declare const Persona: any;

@Component({
  selector: 'app-kyc-wizard',
  standalone: true,
  imports: [MatButton, ButtonLoadingDirective, IconComponent, LottieAnimationComponent],
  templateUrl: './kyc-wizard.component.html',
  styleUrl: './kyc-wizard.component.scss',
})
export class KycWizardComponent implements OnInit {
  // Configuration inputs
  autoContinueOnApproved = input(false);
  fullWidthButtons = input(false);
  largeButtons = input(false);

  // Outputs to parent
  continue = output<void>();
  verifyLater = output<void>();
  statusChange = output<'none' | 'pending' | 'approved' | 'rejected'>();

  private readonly _auth = inject(AuthService);
  private readonly _kycService = inject(KycService);
  private readonly _kycVerificationViewService = inject(KycVerificationViewService);
  private readonly _analytics = inject(Analytics);
  private readonly _logger = inject(LOGGER_FACTORY).create('KycWizard');
  private readonly _platformId = inject<string>(PLATFORM_ID);

  mostRecentKycVerification = toSignal(this._kycVerificationViewService.kycVerification$);
  failed = signal(false);
  loading = signal(false);
  kycVerification = signal<KycVerification | KycVerificationUpdateRTM | null>(null);

  status = signal<'none' | 'pending' | 'approved' | 'rejected'>('none');

  // Pure derivation of the verification status from the current verification.
  private readonly _derivedStatus = computed(() => this.deriveStatus(this.kycVerification()));

  private _clientMap: Record<string, any> = {};

  ngOnInit() {
    // TODO: Remove this when we figure out the Angular Material popover issue
    // https://github.com/angular/components/issues/31504
    try {
      if (isPlatformBrowser(this._platformId)) {
        Array.from(document.querySelectorAll('[popover]')).forEach((e) => e.removeAttribute('popover'));
      }
    } catch (err) {
      this._logger.error('Error removing popover', err);
    }
  }

  constructor() {
    effect(async () => {
      const mostRecentKycVerification = this.mostRecentKycVerification();

      if (
        mostRecentKycVerification &&
        (this.kycVerification()?.kyc_verification_id === mostRecentKycVerification?.kyc_verification_id ||
          (!this.kycVerification() &&
            ['pending', 'approved'].includes(mostRecentKycVerification.kyc_verification_status)))
      ) {
        this.kycVerification.set(mostRecentKycVerification);
      }

      const newStatus = this._derivedStatus();

      if ((newStatus === 'approved' || newStatus === 'rejected') && newStatus !== this.status()) {
        try {
          await this._auth.forceUserReload();
        } catch (err: any) {
          this._logger.error(`Failed to reload user after KYC ${newStatus}`, {
            error: err,
          });
          // Don't block the UI - token will eventually refresh on next request
        }
      }

      if (newStatus === 'approved' && this.autoContinueOnApproved()) {
        // Defer to next microtask to allow parent templates to react before continuing
        Promise.resolve().then(() => this.continue.emit());
      } else if (this.status() !== newStatus) {
        this.status.set(newStatus);
        this.statusChange.emit(newStatus);
      }
    });
  }

  private deriveStatus(
    kycVerification: KycVerification | KycVerificationUpdateRTM | null,
  ): 'none' | 'pending' | 'approved' | 'rejected' {
    if (!kycVerification) return 'none';
    const status = kycVerification.kyc_verification_status;
    if (status === 'abandoned') return 'rejected';
    if (status === 'approved' || status === 'pending' || status === 'rejected') {
      return status;
    }
    return 'pending';
  }

  createClient = (inquiryId: string, sessionToken?: string) => {
    if (this._clientMap[inquiryId]) {
      return this._clientMap[inquiryId];
    }

    return (this._clientMap[inquiryId] = new Persona.Client({
      inquiryId,
      frameAncestors: [environment.webUrl],
      // The JS client reads only `sessionToken`; without it a pending inquiry
      // opens on Persona's "Session expired" screen.
      sessionToken,
      messageTargetOrigin: environment.webUrl,
      environment:
        environment.env === 'local' ? 'sandbox' : (environment.persona.environmentId as 'sandbox' | 'production'),
      onComplete: () => {
        this._kycService.getLatestKycVerification().catch((err) => {
          this._logger.error(`KYC createClient() onComplete() error: ${err.message}`);
          this.failed.set(true);
        });
      },
      onCancel: () => {
        this.failed.set(true);
        this.kycVerification.set(null);
      },
      onError: (error: any) => {
        this._logger.error(`KYC createClient() error: ${error.message}`);
        this.failed.set(true);
      },
    }));
  };

  async startVerification(ignoreExisting = false) {
    try {
      this.loading.set(true);

      let kycVerification = this.kycVerification();

      if (!kycVerification || ignoreExisting) {
        kycVerification = await this._kycService.createKycVerification();
      } else {
        kycVerification = await this._kycService.syncKycVerification(kycVerification.kyc_verification_id);

        // Resuming an expired inquiry at Persona revives it, so an attempt that
        // expired since the wizard loaded starts over instead.
        if (kycVerification.kyc_verification_status === 'abandoned') {
          kycVerification = await this._kycService.createKycVerification();
        }
      }

      this.kycVerification.set(kycVerification);

      this._analytics.trackEvent(AnalyticsEvent.KycVerificationStarted, {
        kyc_verification_id: kycVerification.kyc_verification_id,
      });

      if (kycVerification.kyc_verification_status === 'approved') {
        if (this.autoContinueOnApproved()) {
          this.continue.emit();
        }
      } else {
        this.createClient(
          kycVerification.kyc_verification_entity_id,
          await this._kycService.createResumeToken(kycVerification.kyc_verification_id),
        ).open();
      }
    } catch (err: any) {
      this._logger.error(`KYC startVerification() error: ${err.message}`);
      this.failed.set(true);
    } finally {
      this.loading.set(false);
    }
  }
}
